import { UseFormReturn } from 'react-hook-form'
import {
  BeneficiaryDetails,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { BvnkNetworkConnection } from 'types/bvnkNetwork'
import { PayoutsV2PreAuthRequest } from 'types/payoutsV2'
import { PayoutInstructionTypes, PayoutRequest } from 'types/unifiedPayouts/unifiedPayouts'
import { WalletPayReceiverDetails, WalletsPayRequest } from 'types/wallets'
import { generateReference } from 'utils/utils'
import { FiatSendFormData } from '../../FiatSend/form/fiatSendFormSchema'
import { PayForm } from '../Pay'
import { SourcePayWallet } from '../types'

export const setFormDetails = (
  form: UseFormReturn<PayForm>,
  selectedBeneficiary: BeneficiaryDetails
) => {
  const paymentReference = !!selectedBeneficiary?.beneficiaryPaymentReference
    ? selectedBeneficiary?.beneficiaryPaymentReference
    : generateReference()

  form?.setValue('reference', paymentReference)
}

export const resetFields = (form: UseFormReturn<PayForm>) => {
  const fields: (keyof PayForm)[] = ['reference']

  fields.forEach(field => {
    form?.resetField(field, { defaultValue: null })
  })
}

export const getBvnkNetworkBeneficiaryOptions = (bvnkConnections: BvnkNetworkConnection[]) => {
  return bvnkConnections?.map(connection => {
    return {
      icon: connection?.details?.name || '',
      label: connection?.details?.name,
      value: connection?.id,
      secondLabel: null,
      secondLabelText: connection?.details?.description,
      rowLayout: false,
    }
  })
}

export const isValidEntityTypeAndTransferDestination = (
  beneficiaryTransferDestination: BeneficiaryTransferDestination,
  beneficiaryEntityType: BeneficiaryEntityType,
  transferDestinationFilters: BeneficiaryTransferDestination[],
  isB2cPayoutEnabled: boolean
) => {
  const isValidTransferDestination = transferDestinationFilters.includes(
    beneficiaryTransferDestination
  )
  const isValidEntityType = isB2cPayoutEnabled
    ? true
    : beneficiaryEntityType !== BeneficiaryEntityType.INDIVIDUAL

  return isValidTransferDestination && isValidEntityType
}

export const getPayV1Request = (
  formValues: PayForm,
  selectedFiatBeneficiary: BeneficiaryDetails,
  twoFactorEnabled: boolean
): WalletsPayRequest => {
  const {
    fiat,
    address,
    addressCountry,
    entityType,
    transferDestination,
    firstName,
    lastName,
    dateOfBirth,
    businessName,
  } = selectedFiatBeneficiary || {}
  const { bankAddress, bankName, intermediaryBank, accountNumber, code } = fiat || {}

  const payRequest: WalletsPayRequest = {
    amount: Number(formValues?.amount),
    authorization: {},
    receiver: {
      ...(entityType && { type: entityType }),
      ...(addressCountry && { countryCode: addressCountry }),
      ...(formValues?.reference && { reference: formValues?.reference }),
      ...(transferDestination === BeneficiaryTransferDestination.INTERNATIONAL && {
        ...(address && { address }),
        ...(bankAddress && { bankAddress }),
        ...(intermediaryBank && { intermediaryBank }),
        ...(bankName && { bankName }),
      }),
      ...(accountNumber && { bankAccountNumber: accountNumber }),
      ...(code && { code }),
    } as WalletPayReceiverDetails,
  }

  if (entityType === BeneficiaryEntityType.INDIVIDUAL) {
    payRequest.receiver = {
      ...payRequest?.receiver,
      firstName,
      lastName,
      dateOfBirth,
    }
  } else {
    payRequest.receiver = {
      ...payRequest?.receiver,
      companyName: businessName,
    }
  }

  if (twoFactorEnabled) {
    payRequest.authorization = {
      totp: formValues?.password,
    }
  } else {
    payRequest.authorization = {
      password: formValues?.password,
    }
  }

  return payRequest
}

export const getPayRequest = (formValues: PayForm, selectedWallet: SourcePayWallet) => {
  return {
    walletId: selectedWallet?.walletId,
    amount: {
      value: Number(formValues?.amount),
      currency: selectedWallet?.currency,
    },
    paymentReference: formValues?.reference,
    beneficiary: {
      reference: formValues?.beneficiarySelection,
    },
  } as PayoutsV2PreAuthRequest
}

export const getThirdPartyPayoutRequest = (
  formValues: PayForm,
  selectedWallet: SourcePayWallet
): PayoutRequest => {
  return {
    walletId: selectedWallet?.walletId,
    amount: {
      value: formValues?.amount,
      currency: selectedWallet?.currency,
    },
    paymentReference: formValues?.reference,
    instruction: {
      type: PayoutInstructionTypes?.FIAT,
      beneficiary: {
        reference: formValues?.beneficiarySelection,
      },
    },
  }
}

export const getPayFeeRequest = (
  amount: string,
  formValues: PayForm,
  sourceWallet: SourcePayWallet,
  isPayoutsV2Enabled: boolean
): PayoutRequest => {
  return {
    walletId: isPayoutsV2Enabled
      ? sourceWallet?.walletId
      : sourceWallet?.legacyWalletId?.toString(),
    amount: {
      value: amount,
      currency: sourceWallet?.currency,
    },
    paymentReference: formValues?.reference,
    instruction: {
      type: PayoutInstructionTypes.FIAT,
      beneficiary: {
        reference: formValues?.beneficiarySelection,
      },
    },
  }
}

export const getRecipientName = (beneficiary: BeneficiaryDetails): string => {
  return beneficiary?.entityType === BeneficiaryEntityType.INDIVIDUAL
    ? `${beneficiary?.firstName} ${beneficiary?.lastName}`
    : beneficiary?.businessName
}

interface GetInitialMaxPayoutAmountRequestProps {
  beneficiaryReference?: string
  isPayoutsV2Enabled?: boolean
  paymentReference?: string
  sourcWallet?: SourcePayWallet
}

export const getInitialMaxPayoutAmountRequest = ({
  beneficiaryReference,
  isPayoutsV2Enabled,
  paymentReference,
  sourcWallet,
}: GetInitialMaxPayoutAmountRequestProps): PayoutRequest => {
  return {
    walletId: isPayoutsV2Enabled ? sourcWallet?.walletId : sourcWallet?.legacyWalletId?.toString(),
    amount: {
      value: '1',
      currency: sourcWallet?.currency,
    },
    paymentReference: paymentReference,
    instruction: {
      type: PayoutInstructionTypes.FIAT,
      beneficiary: {
        reference: beneficiaryReference,
      },
    },
  }
}

export const getFiatSendFeePayload = (
  formValues: FiatSendFormData,
  selectedWallet: SourcePayWallet,
  isPayoutsV2Enabled: boolean
): PayoutRequest => {
  return {
    walletId: isPayoutsV2Enabled
      ? selectedWallet?.walletId
      : selectedWallet?.legacyWalletId?.toString(),
    amount: {
      value: formValues?.amount,
      currency: selectedWallet?.currency,
    },
    paymentReference: formValues?.paymentReference,
    instruction: {
      // EUR
      ...(formValues?.paymentMethod && {
        paymentMethod: formValues?.paymentMethod,
      }),
      type: PayoutInstructionTypes.FIAT,
      beneficiary: {
        reference: formValues?.beneficiaryReference,
      },
    },
  }
}
